import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ProductsPage.css';
import ProductCard from './ProductCard';
import productsService from '../../../services/products.service';
import attributesService from '../../../services/attributes.service';
import Header from '../../../components/layout/Header/Header';
import Footer from '../../../components/layout/Footer/Footer';
import categoriesService from '../../../services/categories.service';

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('popularity');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [allColors, setAllColors] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allCategories, setAllCategories] = useState([]);
  // Filters state
  const [filters, setFilters] = useState({
    materials: [],
    colors: [],
    priceRange: { min: 0, max: 1000000 }
  });

  // Fetch products and attributes on mount
  useEffect(() => {
    fetchProducts();
    fetchColors();
    fetchMaterials();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await productsService.getAllProducts();
      if (result.success) {
        setAllProducts(result.products);
      } else {
        setError(result.error || 'Failed to load products');
      }
    } catch (err) {
      setError('An error occurred while loading products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchColors = async () => {
    try {
      const result = await attributesService.getAllColors();
      if (result.success) {
        setAllColors(result.colors);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const result = await attributesService.getAllMaterials();
      if (result.success) {
        setAllMaterials(result.materials);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };
const fetchCategories = async () => {
  try {
    const result = await categoriesService.getAllCategories();
    if (result.success) {
      setAllCategories(result.categories);
    }
  } catch (error) {
    console.error('Error fetching categories:', error);
  }
};
  // Extract unique filter options from products (including variants)
const filterOptions = useMemo(() => {
  const materials = new Set();
  const colors = new Set();

  allProducts.forEach(product => {
    // For products with variants
    if (product.hasVariants && product.variants) {
      product.variants.forEach(variant => {
        if (variant.material) materials.add(variant.material);
        if (variant.color) colors.add(variant.color);
      });
    } else {
      // For products without variants
      if (product.material) materials.add(product.material);
      if (product.color) colors.add(product.color);
    }
  });

  return {
    categories: allCategories.map(cat => cat.name), // Use fetched categories instead
    materials: Array.from(materials).filter(Boolean),
    colors: Array.from(colors).filter(Boolean)
  };
}, [allProducts, allCategories]); // Add allCategories to dependencies

  // Calculate max price from all products (including variants)
  const maxProductPrice = useMemo(() => {
    if (allProducts.length === 0) return 1000000;

    let maxPrice = 0;
    allProducts.forEach(product => {
      if (product.hasVariants && product.variants) {
        product.variants.forEach(variant => {
          if (variant.price > maxPrice) maxPrice = variant.price;
        });
      } else {
        if (product.price > maxPrice) maxPrice = product.price;
      }
    });

    return maxPrice > 0 ? maxPrice : 1000000;
  }, [allProducts]);

  // Update max price when products load
  useEffect(() => {
    if (allProducts.length > 0) {
      setFilters(prev => ({
        ...prev,
        priceRange: {
          min: 0,
          max: maxProductPrice
        }
      }));
    }
  }, [allProducts, maxProductPrice]);

  // Set initial category from URL
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && filterOptions.categories.includes(categoryParam)) {
      setSelectedCategory(categoryParam);
    } else if (categoryParam === null || categoryParam === 'all') {
      setSelectedCategory('all');
      if (categoryParam !== 'all') {
        setSearchParams({ category: 'all' });
      }
    }
  }, [searchParams, filterOptions.categories]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearchParams({ category });
  };

  // Get product price range (for filtering)
  const getProductPriceRange = (product) => {
    if (product.hasVariants && product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      return {
        min: Math.min(...prices),
        max: Math.max(...prices)
      };
    }
    return {
      min: product.price,
      max: product.price
    };
  };

  // Check if product matches material filter (including variants)
  const productMatchesMaterial = (product, selectedMaterials) => {
    if (selectedMaterials.length === 0) return true;

    if (product.hasVariants && product.variants) {
      return product.variants.some(v => selectedMaterials.includes(v.material));
    }
    return selectedMaterials.includes(product.material);
  };

  // Check if product matches color filter (including variants)
  const productMatchesColor = (product, selectedColors) => {
    if (selectedColors.length === 0) return true;

    if (product.hasVariants && product.variants) {
      return product.variants.some(v => selectedColors.includes(v.color));
    }
    return selectedColors.includes(product.color);
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    // Category filter
    if (selectedCategory && selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    // Search filter
    if (searchQuery) {
      result = result.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Material filter (handles variants)
    if (filters.materials.length > 0) {
      result = result.filter(p => productMatchesMaterial(p, filters.materials));
    }

    // Color filter (handles variants)
    if (filters.colors.length > 0) {
      result = result.filter(p => productMatchesColor(p, filters.colors));
    }

    // Price range filter (handles variants)
    result = result.filter(p => {
      const priceRange = getProductPriceRange(p);
      // Product is included if any of its prices fall within the range
      return priceRange.min <= filters.priceRange.max && priceRange.max >= filters.priceRange.min;
    });

    // Sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => {
          const priceA = getProductPriceRange(a).min;
          const priceB = getProductPriceRange(b).min;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        result.sort((a, b) => {
          const priceA = getProductPriceRange(a).max;
          const priceB = getProductPriceRange(b).max;
          return priceB - priceA;
        });
        break;
      case 'newest':
        result.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        break;
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default: // popularity
        result.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    }

    return result;
  }, [allProducts, selectedCategory, searchQuery, filters, sortBy]);

  // Autocomplete suggestions
  const autocompleteSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];

    let productsToSearch = allProducts;
    if (selectedCategory && selectedCategory !== 'all') {
      productsToSearch = allProducts.filter(p => p.category === selectedCategory);
    }

    return productsToSearch
      .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .slice(0, 5);
  }, [searchQuery, allProducts, selectedCategory]);

  // Toggle filter
  const toggleFilter = (filterType, value) => {
    setFilters(prev => {
      const current = prev[filterType];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      materials: [],
      colors: [],
      priceRange: { min: 0, max: maxProductPrice }
    });
    setSearchQuery('');
  };

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    return (
      filters.materials.length +
      filters.colors.length +
      (filters.priceRange.min > 0 || filters.priceRange.max < maxProductPrice ? 1 : 0) +
      (searchQuery ? 1 : 0)
    );
  }, [filters, searchQuery, maxProductPrice]);

  // Handle product click
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Get color hex from allColors list
  const getColorHex = (colorName) => {
    const color = allColors.find(c => c.value === colorName || c.label === colorName);
    return color?.hex || '#808080';
  };

  if (error) {
    return (
      <div className="products-page">
        <div className="products-page-container">
          <div className="error-message">
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <button onClick={fetchProducts} className="retry-button">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="products-page">
        <Header />
        <div className="products-page-container">
          {/* Page Header */}
          <div className="products-page__header">
            <p className="products-page__subtitle">
              Découvrez notre collection complète de mobilier de bureau professionnel
            </p>
          </div>

          {/* Main Layout */}
          <div className="products-page__main">
            {/* Sidebar Filters */}
            <aside className={`products-sidebar ${sidebarOpen ? 'open' : ''}`}>
              <div className="sidebar__header">
                <h3 className="sidebar__title">Filtres</h3>
                {activeFiltersCount > 0 && (
                  <button className="sidebar__clear" onClick={clearAllFilters}>
                    Effacer tout ({activeFiltersCount})
                  </button>
                )}
              </div>

              {/* Search Filter */}
              <div className="filter-section">
                <h4 className="filter-section__title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  Recherche
                </h4>
                <div className="search-filter">
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    className="search-filter__input"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowAutocomplete(true);
                    }}
                    onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
                  />
                  <svg className="search-filter__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>

                  {showAutocomplete && autocompleteSuggestions.length > 0 && (
                    <div className="search-autocomplete">
                      {autocompleteSuggestions.map(product => (
                        <div
                          key={product.id}
                          className="autocomplete-item"
                          onClick={() => {
                            setSearchQuery(product.name);
                            setShowAutocomplete(false);
                          }}
                        >
                          <svg className="autocomplete-item__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                          <span className="autocomplete-item__text">{product.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Price Range Filter */}
              <div className="filter-section">
                <h4 className="filter-section__title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  Prix (DA)
                </h4>
                <div className="price-range">
                  <div className="price-range__slider">
                    <input
                      type="range"
                      className="price-slider"
                      min="0"
                      max={maxProductPrice}
                      step="5000"
                      value={filters.priceRange.max}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        priceRange: { ...prev.priceRange, max: Number(e.target.value) }
                      }))}
                    />
                  </div>
                  <div className="price-range__display">
                    <span>{filters.priceRange.min.toLocaleString()} DA</span>
                    <span>{filters.priceRange.max.toLocaleString()} DA</span>
                  </div>
                </div>
              </div>

              {/* Material Filter */}
              {filterOptions.materials.length > 0 && (
                <div className="filter-section">
                  <h4 className="filter-section__title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                    Matériaux
                  </h4>
                  <div className="checkbox-filter">
                    {filterOptions.materials.map(material => (
                      <label key={material} className="checkbox-item">
                        <input
                          type="checkbox"
                          className="checkbox-item__input"
                          checked={filters.materials.includes(material)}
                          onChange={() => toggleFilter('materials', material)}
                        />
                        <span className="checkbox-item__label">
                          {material}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Filter */}
              {filterOptions.colors.length > 0 && (
                <div className="filter-section">
                  <h4 className="filter-section__title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="13.5" cy="6.5" r=".5" />
                      <circle cx="17.5" cy="10.5" r=".5" />
                      <circle cx="8.5" cy="7.5" r=".5" />
                      <circle cx="6.5" cy="12.5" r=".5" />
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
                    </svg>
                    Couleurs
                  </h4>
                  <div className="color-filter">
                    {filterOptions.colors.map(color => (
                      <div
                        key={color}
                        className={`color-option ${filters.colors.includes(color) ? 'active' : ''}`}
                        style={{
                          backgroundColor: getColorHex(color),
                          border: getColorHex(color) === '#FFFFFF' || getColorHex(color).toLowerCase() === '#fff'
                            ? '2px solid #e0e0e0'
                            : 'none'
                        }}
                        onClick={() => toggleFilter('colors', color)}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}
            </aside>

            {/* Main Content */}
            <div className="products-content">
              {/* Toolbar */}
              <div className="products-toolbar2">
                <div className="toolbar__left">
                  <div className="toolbar__count">
                    <strong>{filteredProducts.length}</strong> produits trouvés
                  </div>

                  {/* Category Selection */}
                  <div className="toolbar__category">
                    <span className="toolbar__category-label">Catégorie:</span>
                    <select
                      className="category-select"
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="all">Toutes les catégories</option>
                      {filterOptions.categories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By */}
                  <div className="toolbar__sort">
                    <span className="toolbar__sort-label">Trier par:</span>
                    <select
                      className="sort-select2"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="popularity">Popularité</option>
                      <option value="newest">Plus récent</option>
                      <option value="price-asc">Prix croissant</option>
                      <option value="price-desc">Prix décroissant</option>
                      <option value="rating">Meilleure note</option>
                    </select>
                  </div>
                </div>

                <div className="toolbar__right">
                  {/* View Toggle */}
                  <div className="toolbar__view-toggle">
                    <button
                      className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                      title="Vue grille"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                    </button>
                    <button
                      className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                      title="Vue liste"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Filters */}
              {activeFiltersCount > 0 && (
                <div className="active-filters">
                  <span className="active-filters__label">Filtres actifs:</span>
                  {searchQuery && (
                    <div className="filter-tag">
                      Recherche: "{searchQuery}"
                      <button
                        className="filter-tag__remove"
                        onClick={() => setSearchQuery('')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {filters.materials.map(mat => (
                    <div key={mat} className="filter-tag">
                      {mat}
                      <button
                        className="filter-tag__remove"
                        onClick={() => toggleFilter('materials', mat)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {filters.colors.map(col => (
                    <div key={col} className="filter-tag">
                      {col}
                      <button
                        className="filter-tag__remove"
                        onClick={() => toggleFilter('colors', col)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button className="clear-all-filters" onClick={clearAllFilters}>
                    Tout effacer
                  </button>
                </div>
              )}

              {/* Products Grid/List */}
              {loading ? (
                <div className="products-loading">
                  <div className="loading-spinner"></div>
                  <p>Chargement des produits...</p>
                </div>
              ) : filteredProducts.length > 0 ? (
                <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                  {filteredProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      viewMode={viewMode}
                      onProductClick={handleProductClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <div className="no-results__icon">🔍</div>
                  <h3 className="no-results__title">Aucun produit trouvé</h3>
                  <p className="no-results__message">
                    Essayez de modifier vos filtres ou votre recherche
                  </p>
                  <button className="no-results__btn" onClick={clearAllFilters}>
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Filters Toggle */}
        <button
          className="mobile-filters-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="14" />
            <line x1="20" y1="10" x2="20" y2="3" />
          </svg>
          Filtres
          {activeFiltersCount > 0 && (
            <span className="filters-badge">{activeFiltersCount}</span>
          )}
        </button>

        {/* Sidebar Overlay for Mobile */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay visible"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default ProductsPage;